import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  useTheme,
  useMediaQuery,
  styled,
  Tab,
  Tabs,
  Stack,
  Button,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import {
  getBidDetails,
  getBillingMilestones,
  updateBOQPrice,
  saveBillingMilestones,
} from "../../services/rfqService";
import { deletePaymentGroup } from "../../services/paymentGroupService";
import { updateUserData } from "../../utils/CommonFunction";
import { useTokenValidation } from "../../components/hooks/useTokenValidation";
import SecurityPolicyModal from "../../components/SecurityPolicyModal";
import { postFormData, getApiData } from "../../utils/APIHelper";
import { toast } from "react-toastify";
import { ReactComponent as LeftArrowIcon } from "../../assets/svg/LeftArrowIcon.svg";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ConfirmModel from "../../components/ConfirmModel";
import CommissionInfo from "./components/CommissionInfo";
import VillaProject from "./components/VillaProject";
import BillingMilestones from "./components/BillingMilestones";
import ProposalResponse from "./components/ProposalResponse";
import BOQTabsView from "./components/BOQTabsView";
import Review from "./components/Review";
import ProjectSidebar from "./components/ProjectSidebar";

const StyledTabs = styled(Tabs)({
  display: "flex",
  gap: "8px",
  padding: "0",
  minHeight: "36px",
  "& .MuiTabs-flexContainer": {
    alignItems: "center",
  },
});

// Pill tabs — same compact look as the admin portal's design scope
// stepper (rounded-full pills, 36px tall, small number bubble).
const StyledTab = styled(Tab)(({ selected }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "0 14px",
  minHeight: "36px",
  height: "36px",
  fontSize: "13px",
  borderRadius: "999px",
  fontFamily: "Inter Tight",
  fontWeight: 500,
  textTransform: "none",
  backgroundColor: selected ? "#0D0D0D" : "#F7F7F7",
  color: selected ? "#FFFFFF !important" : "#949DAA !important",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: selected ? "#0D0D0D" : "#EDEDED",
    color: selected ? "#FFFFFF" : "#000",
  },
  marginRight: "8px",
}));

const TabNumber = styled("span")(({ selected }) => ({
  width: "22px",
  height: "22px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  fontSize: "11px",
  fontWeight: 600,
  flexShrink: 0,
  backgroundColor: selected ? "#3d3d3d" : "#FFFFFF",
  color: selected ? "#FFFFFF" : "#949DAA",
  border: "none !important",
}));

export default function RFQPricing() {
  const { id, bidId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [isMilestoneLoading, setIsMilestoneLoading] = useState(false);
  const [savingBoqId, setSavingBoqId] = useState(null);

  const [bidDetails, setBidDetails] = useState(null);
  const [roomBOQs, setRoomBOQs] = useState(null);
  const [proposalDescription, setProposalDescription] = useState("");
  const [timeline, setTimeline] = useState({
    Approvals: "",
    "Demolition & Site Prep": "",
    Construction: "",
    "Finishing & Handover": "",
  });
  const [billingMilestones, setBillingMilestones] = useState([]);
  const [billingMilestonesData, setBillingMilestonesData] = useState(null);
  const [proposalImages, setProposalImages] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [categoryData, setCategoryData] = useState(null);
  const [editingBOQPrice, setEditingBOQPrice] = useState(null);
  const [boqPriceInputs, setBoqPriceInputs] = useState({});
  const [originalMilestones, setOriginalMilestones] = useState([]);
  const [hasMilestoneChanges, setHasMilestoneChanges] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [activeMainTab, setActiveMainTab] = useState(0);
  const [isSavingProposal, setIsSavingProposal] = useState(false);
  const [isSavingBOQs, setIsSavingBOQs] = useState(false);
  const [isSavingMilestones, setIsSavingMilestones] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  // Scoped loader for the Review tab refresh. Lets us keep the tabs view
  // mounted (no full-screen blank) while we re-fetch bid details to decide
  // whether the "Submit Bid" CTA should show.
  const [isReviewLoading, setIsReviewLoading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { showSecurityModal, handleSecurityModalClose } = useTokenValidation();

  const tabRoutes = ["proposal", "boq", "milestones", "summary"];

  const fetchData = async ({ background = false } = {}) => {
    if (background) {
      setIsReviewLoading(true);
    } else {
      setLoading(true);
    }
    try {
      const [bidResponse, milestonesResponse] = await Promise.all([
        getBidDetails(id, bidId),
        getBillingMilestones(bidId),
      ]);
      console.log("bidResponse", bidResponse);

      setBidDetails(bidResponse?.data?.[0] || null);
      setRoomBOQs(bidResponse?.data?.[0]?.scope_boqs || null);
      setBillingMilestonesData(milestonesResponse || null);

      if (bidResponse?.data?.[0]?.timeline) {
        setTimeline(bidResponse.data[0].timeline);
      }
      if (bidResponse?.data?.[0]?.scope_of_work) {
        setProposalDescription(bidResponse.data[0].scope_of_work);
      }
      if (milestonesResponse?.billing_milestones) {
        setBillingMilestones(milestonesResponse?.billing_milestones);
        setOriginalMilestones(
          JSON.parse(JSON.stringify(milestonesResponse?.billing_milestones))
        );
      }

      // Hydrate proposalImages with the full project_image array so the
      // form can pick the tag-less primary image (and ignore tagged
      // ones, e.g. customer-provided reference shots).
      const projectImages = bidResponse?.data?.[0]?.project_image;
      if (Array.isArray(projectImages) && projectImages.length > 0) {
        setProposalImages(projectImages);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      if (background) {
        setIsReviewLoading(false);
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    updateUserData();
  }, []);

  useEffect(() => {
    if (id && bidId) {
      fetchData();
    }
  }, [id, bidId]);

  const handleProposalImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const formData = new FormData();
    files.forEach((file, index) => {
      formData.append(`file_${index + 1}`, file);
    });
    formData.append("type", "project");
    formData.append("project_id", bidId);

    try {
      const response = await postFormData("api/project/files", formData);
      if (response.success && response.images) {
        setProposalImages((prev) => [...prev, ...response.images]);
      }
    } catch (error) {
      console.error("Error uploading proposal images:", error);
    }
  };

  const handleDeleteProposalImage = async (imageId) => {
    try {
      const response = await getApiData(
        `api/project/delete-files/${imageId}`,
        "POST"
      );
      if (response.success) {
        setProposalImages(proposalImages.filter((img) => img.id !== imageId));
      }
    } catch (error) {
      console.error("Error deleting proposal image:", error);
    }
  };

  const calculateTotalWeeks = () => {
    const values = Object.values(timeline).map((val) => parseInt(val) || 0);
    return values.reduce((sum, val) => sum + val, 0);
  };

  const refreshBillingMilestones = async () => {
    setIsMilestoneLoading(true);
    try {
      const milestonesResponse = await getBillingMilestones(bidId);
      setBillingMilestonesData(milestonesResponse || null);
      if (milestonesResponse?.billing_milestones) {
        setBillingMilestones(milestonesResponse.billing_milestones);
        setOriginalMilestones(
          JSON.parse(JSON.stringify(milestonesResponse.billing_milestones))
        );
      }
    } catch (error) {
      console.error("Error refreshing billing milestones:", error);
    } finally {
      setIsMilestoneLoading(false);
    }
  };

  const validateMilestones = () => {
    for (let i = 0; i < billingMilestones.length; i++) {
      const milestone = billingMilestones[i];

      // Skip validation for existing milestones (ones with string ID)
      const isExistingMilestone =
        milestone.id && typeof milestone.id === "string";
      if (isExistingMilestone) {
        continue;
      }

      // Only validate newly added milestones
      if (!milestone.name || milestone.name.trim() === "") {
        toast.error(`Please fill milestone name for milestone ${i + 1}`);
        return false;
      }
      if (
        !milestone.percentage ||
        milestone.percentage === "" ||
        isNaN(parseFloat(milestone.percentage))
      ) {
        toast.error(`Please fill valid percentage for milestone ${i + 1}`);
        return false;
      }
    }
    return true;
  };

  const saveMilestoneChanges = async () => {
    if (!validateMilestones()) {
      return false;
    }

    setIsSavingMilestones(true);
    try {
      const proposalIds = [billingMilestonesData?.proposal_id];

      // Find only changed or newly added milestones
      const changedMilestones = [];

      billingMilestones.forEach((currentMilestone, index) => {
        const originalMilestone = originalMilestones[index];

        // Check if this is a new milestone (no original counterpart)
        if (!originalMilestone) {
          changedMilestones.push({
            name: currentMilestone.name,
            description: currentMilestone.description || "",
            percentage: parseFloat(currentMilestone.percentage),
          });
        } else {
          // Check if any field has changed
          const hasChanged =
            currentMilestone.name !== originalMilestone.name ||
            parseFloat(currentMilestone.percentage) !==
              parseFloat(originalMilestone.percentage);

          if (hasChanged) {
            changedMilestones.push({
              name: currentMilestone.name,
              description: currentMilestone.description || "",
              percentage: parseFloat(currentMilestone.percentage),
            });
          }
        }
      });

      // Only send request if there are changes
      if (changedMilestones.length > 0) {
        await saveBillingMilestones(proposalIds, changedMilestones);
        await refreshBillingMilestones();
        setHasMilestoneChanges(false);
        toast.success("Milestones saved successfully!");
        return true;
      } else {
        return true; // No changes but still allow navigation
      }
    } catch (error) {
      console.error("Error saving milestones:", error);
      toast.error("Error saving milestones. Please try again.");
      return false;
    } finally {
      setIsSavingMilestones(false);
    }
  };

  const handleSaveProposal = async () => {
    const validateTimeline = () => {
      const errors = [];

      // Only validate timeline fields that have values (not required)
      // Allow empty fields, 0, and positive integers
      Object.entries(timeline).forEach(([key, value]) => {
        const stringValue = String(value || "");
        if (stringValue.trim() !== "") {
          // Check if value is a non-negative integer (0 or positive)
          const numValue = parseInt(stringValue);
          if (isNaN(numValue) || numValue < 0 || !Number.isInteger(numValue)) {
            errors.push(`${key} must be a non-negative integer`);
          }
        }
      });

      return errors;
    };

    const validationErrors = validateTimeline();

    if (validationErrors.length > 0) {
      validationErrors.forEach((error) => toast.error(error));
      return false;
    }

    setIsSavingProposal(true);

    try {
      const payload = {
        proposal_id: bidDetails?.proposal_id,
      };

      // Only include scope_of_work if it has a value
      if (proposalDescription && proposalDescription.trim() !== "") {
        payload.scope_of_work = proposalDescription;
      }

      // Only include timeline fields that have values (including 0)
      const timelineWithValues = Object.fromEntries(
        Object.entries(timeline)
          .filter(
            ([key, value]) =>
              value !== "" && value !== null && value !== undefined
          )
          .map(([key, value]) => [key, parseInt(value)])
      );

      if (Object.keys(timelineWithValues).length > 0) {
        payload.timeline = timelineWithValues;
      }

      const response = await getApiData("api/bid/update", "POST", payload);

      if (response.success) {
        toast.success(response.message || "Proposal saved successfully!");
        return true;
      } else {
        toast.error("Failed to save proposal. Please try again.");
        return false;
      }
    } catch (error) {
      console.error("Error saving proposal:", error);
      toast.error("Error saving proposal. Please try again.");
      return false;
    } finally {
      setIsSavingProposal(false);
    }
  };

  const collectAllBOQs = () => {
    const boqs = [];
    const seen = new Set();

    const pushBoq = (boq) => {
      if (!boq?.id || seen.has(boq.id)) return;
      seen.add(boq.id);
      boqs.push(boq);
    };

    if (roomBOQs?.rooms) {
      for (const room of roomBOQs.rooms) {
        for (const inclusion of room.inclusions || []) {
          for (const boq of inclusion.boqs || []) {
            pushBoq(boq);
          }
        }
      }
    }

    if (categoryData?.inclusions) {
      for (const inclusion of categoryData.inclusions) {
        for (const room of inclusion.rooms || []) {
          for (const boq of room.boqs || []) {
            pushBoq(boq);
          }
        }
      }
    }

    return boqs;
  };

  const saveAllBOQPrices = async () => {
    const allBoqs = collectAllBOQs().filter(
      (boq) => boq.procurement !== "User" && boq.procurement !== "Reno"
    );

    if (allBoqs.length === 0) {
      return true;
    }

    setIsSavingBOQs(true);
    try {
      await Promise.all(
        allBoqs.map((boq) => {
          const typedPrice = boqPriceInputs[boq.id];
          const savedPrice =
            boq.type === "Activities" ? boq.manpower_rate : boq.unit_cost;
          const price =
            typedPrice !== undefined && typedPrice !== ""
              ? typedPrice
              : savedPrice;

          const qtyKey =
            boq.type === "Activities"
              ? `${boq.id}_days`
              : `${boq.id}_quantity`;
          const typedQty = boqPriceInputs[qtyKey];
          const savedQty =
            boq.type === "Activities" ? boq.days : boq.quantity;
          const quantity =
            typedQty !== undefined && typedQty !== ""
              ? typedQty
              : savedQty;

          return updateBOQPrice(
            bidId,
            boq.id,
            parseFloat(price) || 0,
            quantity != null && quantity !== ""
              ? parseInt(quantity, 10)
              : null,
            boq.type
          );
        })
      );

      setEditingBOQPrice(null);
      setBoqPriceInputs({});

      if (activeTab === 0) {
        await fetchRoomData();
      } else if (activeTab === 1) {
        await fetchCategoryData();
      }
      await refreshBillingMilestones();

      return true;
    } catch (error) {
      console.error("Error saving BOQ prices:", error);
      toast.error("Error saving BOQ prices. Please try again.");
      return false;
    } finally {
      setIsSavingBOQs(false);
    }
  };

  const handleNextTab = async () => {
    if (activeMainTab === 0) {
      // Tab 1: Save proposal response
      const saved = await handleSaveProposal();
      if (saved) {
        setActiveMainTab(1);
      }
    } else if (activeMainTab === 1) {
      // Tab 2: Persist all BOQ prices, then go to next tab
      const saved = await saveAllBOQPrices();
      if (saved) {
        setActiveMainTab(2);
      }
    } else if (activeMainTab === 2) {
      // Tab 3: Save milestones then go to Review tab
      const saved = await saveMilestoneChanges();
      if (saved) {
        setActiveMainTab(3);
        // Refresh bid details when going to Review tab (scoped loader so the
        // tabs view stays mounted instead of a full-screen reload).
        await fetchData({ background: true });
      }
    }
  };

  const handleTabChange = async (e, newValue) => {
    setActiveMainTab(newValue);
    // Refresh bid details when switching to Review tab (scoped loader so the
    // tabs view stays mounted instead of a full-screen reload).
    if (newValue === 3) {
      await fetchData({ background: true });
    }
  };

  const addMilestone = () => {
    const newMilestone = {
      name: "",
      percentage: "",
      description: "",
      amount: "-",
    };
    setBillingMilestones([...billingMilestones, newMilestone]);
    setHasMilestoneChanges(true);
  };

  const deleteMilestone = async (index) => {
    const milestone = billingMilestones[index];
    if (milestone.id && typeof milestone.id === "string") {
      // Existing milestone - call API using payment-group endpoint
      setIsMilestoneLoading(true);
      try {
        await deletePaymentGroup(milestone.id);
        setBillingMilestones(billingMilestones.filter((_, i) => i !== index));
        await refreshBillingMilestones();
      } catch (error) {
        console.error("Error deleting milestone:", error);
      } finally {
        setIsMilestoneLoading(false);
      }
    } else {
      // Local milestone (newly added without ID) - just remove from state
      setBillingMilestones(billingMilestones.filter((_, i) => i !== index));
      setHasMilestoneChanges(true);
    }
  };

  const updateMilestone = (index, field, value) => {
    const updatedMilestones = [...billingMilestones];
    updatedMilestones[index] = {
      ...updatedMilestones[index],
      [field]: value,
    };
    setBillingMilestones(updatedMilestones);
    setHasMilestoneChanges(true);
  };

  const calculateMilestoneAmount = (percentage) => {
    const totalAmount =
      billingMilestonesData?.total_amount || bidDetails.proposal?.amount || 0;
    return totalAmount * (percentage / 100);
  };

  const handleEditBOQPrice = (boq) => {
    setEditingBOQPrice(boq.id);
    if (boq.type === "Activities") {
      setBoqPriceInputs({
        ...boqPriceInputs,
        [boq.id]: boq.manpower_rate ?? "",
        [`${boq.id}_days`]: boq.days ?? "",
      });
    } else {
      setBoqPriceInputs({
        ...boqPriceInputs,
        [boq.id]: boq.unit_cost ?? "",
        [`${boq.id}_quantity`]: boq.quantity ?? "",
      });
    }
  };

  const handleSaveBOQPrice = async (boqId) => {
    // Find the BOQ to get its type
    let boq = null;
    if (activeTab === 0 && roomBOQs?.rooms) {
      for (const room of roomBOQs.rooms) {
        for (const inclusion of room.inclusions || []) {
          boq = inclusion.boqs?.find((b) => b.id === boqId);
          if (boq) break;
        }
        if (boq) break;
      }
    } else if (activeTab === 1 && categoryData?.inclusions) {
      for (const inclusion of categoryData.inclusions) {
        for (const room of inclusion.rooms || []) {
          boq = room.boqs?.find((b) => b.id === boqId);
          if (boq) break;
        }
        if (boq) break;
      }
    }

    if (!boq) return;

    const typedPrice = boqPriceInputs[boqId];
    const savedPrice =
      boq.type === "Activities" ? boq.manpower_rate : boq.unit_cost;
    const price =
      typedPrice !== undefined && typedPrice !== ""
        ? typedPrice
        : savedPrice;

    const qtyKey =
      boq.type === "Activities" ? `${boqId}_days` : `${boqId}_quantity`;
    const typedQty = boqPriceInputs[qtyKey];
    const savedQty = boq.type === "Activities" ? boq.days : boq.quantity;
    let quantity =
      typedQty !== undefined && typedQty !== "" ? typedQty : savedQty;

    // 0 is a legitimate price (a free or already-included item), so gate on
    // "a number was entered" rather than truthiness: `price && price > 0`
    // rejected both the number 0 and the string "0".
    const priceEntered =
      price !== undefined && price !== null && String(price).trim() !== "";
    const numericPrice = Number(price);
    const priceIsValid =
      priceEntered && Number.isFinite(numericPrice) && numericPrice >= 0;

    if (priceIsValid) {
      setSavingBoqId(boqId);
      try {
        await updateBOQPrice(
          bidId,
          boqId,
          numericPrice,
          quantity
            ? boq.type === "Activities"
              ? parseInt(quantity)
              : parseInt(quantity)
            : null,
          boq.type
        );
        setEditingBOQPrice(null);
        setBoqPriceInputs({});
        // Refresh data based on current tab
        if (activeTab === 0) {
          await fetchRoomData();
          await refreshBillingMilestones();
        } else if (activeTab === 1) {
          await fetchCategoryData();
          await refreshBillingMilestones();
        }
      } catch (error) {
        console.error("Error saving BOQ price:", error);
      } finally {
        setSavingBoqId(null);
      }
    }
  };

  const handleCancelBOQPriceEdit = () => {
    setEditingBOQPrice(null);
    setBoqPriceInputs({});
  };

  const handleBOQPriceChange = (boqId, value) => {
    setBoqPriceInputs({
      ...boqPriceInputs,
      [boqId]: value,
    });
  };

  const handleApproveBid = async () => {
    try {
      const response = await getApiData("api/project/status", "PUT", {
        project_id: bidId,
        status: "approve",
      });
      if (response.success) {
        toast.success("Bid Approved Successfully");
        setIsApproved(false);
        // Redirect to /rfqs on the "Submitted" tab (approved-by-contractor),
        // where the just-submitted bid now lives.
        navigate("/rfqs", { state: { tab: "awarded" } });
      } else {
        toast.error(response.message || "Failed to approve bid");
        setIsApproved(false);
      }
    } catch (error) {
      console.error("Error approving bid:", error);
      toast.error("Error approving bid. Please try again.");
      setIsApproved(false);
    }
  };

  const fetchCategoryData = async () => {
    try {
      const response = await getBidDetails(id, bidId, "by_category");
      setCategoryData(response?.data?.[0]?.scope_boqs || null);
      console.log("Category data refreshed:", response?.data);
    } catch (error) {
      console.error("Error fetching category data:", error);
    }
  };

  const fetchRoomData = async () => {
    try {
      const response = await getBidDetails(id, bidId);
      setRoomBOQs(response?.data?.[0]?.scope_boqs || null);
    } catch (error) {
      console.error("Error fetching room data:", error);
    }
  };

  const calculateProjectMetrics = () => {
    if (!roomBOQs?.rooms)
      return { rooms: 0, inclusions: 0, totalItems: 0, priced: 0 };

    const rooms = roomBOQs.rooms.length;
    const inclusions = roomBOQs.rooms.reduce(
      (sum, room) => sum + room.inclusions.length,
      0
    );
    const totalItems = roomBOQs.rooms.reduce(
      (sum, room) =>
        sum +
        room.inclusions.reduce(
          (roomSum, inclusion) => roomSum + inclusion.boqs.length,
          0
        ),
      0
    );

    // Use covered_boqs from scope_boqs object
    const priced = bidDetails?.scope_boqs?.covered_boqs || 0;

    return { rooms, inclusions, totalItems, priced };
  };

  const metrics = calculateProjectMetrics();

  const renderCommissionInfo = () => <CommissionInfo bidDetails={bidDetails} />;

  const renderVillaProject = () => (
    <VillaProject bidDetails={bidDetails} metrics={metrics} />
  );

  const renderBillingMilestones = () => {
    const isDisabled =
      bidDetails?.status !== "awaiting-contractor-confirmation";

    return (
      <BillingMilestones
        billingMilestonesData={billingMilestonesData}
        billingMilestones={billingMilestones}
        originalMilestones={originalMilestones}
        hasMilestoneChanges={hasMilestoneChanges}
        isMilestoneLoading={isMilestoneLoading}
        addMilestone={addMilestone}
        deleteMilestone={deleteMilestone}
        updateMilestone={updateMilestone}
        saveMilestoneChanges={saveMilestoneChanges}
        calculateMilestoneAmount={calculateMilestoneAmount}
        isDisabled={isDisabled}
        hideSaveButton={true}
      />
    );
  };

  const renderProposalResponse = () => {
    const isDisabled =
      bidDetails?.status !== "awaiting-contractor-confirmation";

    return (
      <ProposalResponse
        proposalImages={proposalImages}
        proposalDescription={proposalDescription}
        setProposalDescription={setProposalDescription}
        timeline={timeline}
        setTimeline={setTimeline}
        calculateTotalWeeks={calculateTotalWeeks}
        handleProposalImageUpload={handleProposalImageUpload}
        handleDeleteProposalImage={handleDeleteProposalImage}
        proposalId={bidDetails?.proposal_id}
        isDisabled={isDisabled}
        hideSaveButton={true}
      />
    );
  };

  const renderBOQTabsView = () => {
    const isDisabled =
      bidDetails?.status !== "awaiting-contractor-confirmation";

    return (
      <BOQTabsView
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        roomBOQs={roomBOQs}
        categoryData={categoryData}
        boqPriceInputs={boqPriceInputs}
        savingBoqId={savingBoqId}
        handleSaveBOQPrice={handleSaveBOQPrice}
        handleBOQPriceChange={handleBOQPriceChange}
        fetchCategoryData={fetchCategoryData}
        fetchRoomData={fetchRoomData}
        isDisabled={isDisabled}
      />
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!bidDetails) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Typography variant="h6">Bid details not found</Typography>
      </Box>
    );
  }

  const renderTabContent = () => {
    switch (activeMainTab) {
      case 0:
        return renderProposalResponse();
      case 1:
        return renderBOQTabsView();
      case 2:
        return (
          <>
            {renderCommissionInfo()}
            {renderBillingMilestones()}
          </>
        );
      case 3:
        if (isReviewLoading) {
          return (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "40vh",
              }}
            >
              <CircularProgress />
            </Box>
          );
        }
        return (
          <Review
            bidDetails={bidDetails}
            metrics={metrics}
            roomBOQs={roomBOQs}
            billingMilestones={billingMilestones}
            calculateMilestoneAmount={calculateMilestoneAmount}
            billingMilestonesData={billingMilestonesData}
            timeline={timeline}
          />
        );
      default:
        return null;
    }
  };

  const modalStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    bgcolor: "background.paper",
    padding: "0 !important",
    overflowY: "scroll",
    margin: "20px 0",
    height: "100%",
    "&::-webkit-scrollbar": {
      width: "0px",
      background: "transparent",
    },
    scrollbarWidth: "none", // Firefox
    msOverflowStyle: "none", // IE and Edge
  };

  const isDisabled = bidDetails?.status !== "awaiting-contractor-confirmation";

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "row" }}>
      {/* Main Content Wrapper */}
      <Box
        sx={{
          typography: "body1",
          backgroundColor: "#fff",
          height: "100%",
          borderRadius: "6px",
          fontFamily: "Inter Tight",
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <SecurityPolicyModal
          open={showSecurityModal}
          onConfirm={handleSecurityModalClose}
        />
        <>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-start",
              alignItems: "center",
              padding: "3vh 2.5vw 1.5vh",
              gap: "10px",
              background:
                "linear-gradient(180deg, rgba(77, 107, 246, 0.30) 0%, rgba(255, 255, 255, 0.00) 100%)",
              flexShrink: 0,

            }}
          >
            <LeftArrowIcon
              onClick={() => navigate("/rfqs")}
              style={{ cursor: "pointer" }}
            />
            <div
              style={{
                color: "#0D0D0E",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              {bidDetails?.name}
            </div>
            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              {activeMainTab === 3 &&
                (isReviewLoading ? (
                  <CircularProgress size={20} sx={{ color: "#4D6BF6" }} />
                ) : (
                  bidDetails?.status ===
                    "awaiting-contractor-confirmation" && (
                    <button
                      style={{
                        width: "fit-content",
                        backgroundColor: "#4D6BF6",
                        borderRadius: "12px",
                        fontSize: "16px",
                        fontWeight: "600",
                        boxShadow: "none",
                        padding: isMobile ? "1.5vh 10vw" : "1vh 1vw",
                        border: "1px solid transparent",
                        color: "#fff",
                        cursor: "pointer",
                        fontFamily: "Inter Tight",
                      }}
                      onClick={() => setIsApproved(true)}
                    >
                      Submit Bid
                    </button>
                  )
                ))}

              {!isMobile && activeMainTab !== 3 && (
                <button
                  onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                  style={{
                    width: "36px",
                    height: "36px",
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    border: "1px solid #E5E7EB",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s ease",
                  }}
                  title={
                    isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"
                  }
                >
                  {isSidebarCollapsed ? (
                    <ChevronLeftIcon
                      sx={{ fontSize: "20px", color: "#6B7280" }}
                    />
                  ) : (
                    <ChevronRightIcon
                      sx={{ fontSize: "20px", color: "#6B7280" }}
                    />
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Tabs + Sidebar Flex Row Container */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "row",
              overflowY: "hidden",
            }}
          >
            {/* Main Content Area */}
            <Box
              sx={{
                flex: 1,
                paddingLeft: "2vw",
                paddingRight: "2vw",
                paddingBottom: "1.5rem",
              }}
            >
              <Box sx={modalStyle}>
                <Stack direction="row" justifyContent="space-between">
                  <StyledTabs
                    value={activeMainTab}
                    variant="scrollable"
                    onChange={handleTabChange}
                    aria-label="Styled Tabs"
                    sx={{
                      "& .MuiTabs-indicator": {
                        background: "none",
                      },
                    }}
                  >
                    {tabRoutes.map((route, index) => {
                      const labels = {
                        proposal: "Proposal Response",
                        boq: "BOQ",
                        milestones: "Billing Milestones",
                        summary: "Review",
                      };
                      const label = labels[route] || route;

                      return (
                        <StyledTab
                          key={index}
                          label={
                            <Box display="flex" alignItems="center" gap={1}>
                              <TabNumber
                                selected={activeMainTab === index}
                              >{`0${index + 1}`}</TabNumber>
                              <span>{label}</span>
                            </Box>
                          }
                          disabled={false}
                          sx={{
                            cursor: "pointer",
                          }}
                        />
                      );
                    })}
                  </StyledTabs>
                </Stack>
                {renderTabContent()}

                {/* Next/Save Button at bottom */}
                {activeMainTab !== 3 && !isDisabled && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      padding: "0",
                      border: "none",
                      boxShadow: "none",
                      marginTop: "0",
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={handleNextTab}
                      disabled={
                        (activeMainTab === 0 && isSavingProposal) ||
                        (activeMainTab === 1 && isSavingBOQs) ||
                        (activeMainTab === 2 && isSavingMilestones)
                      }
                      sx={{
                        backgroundColor: "#4D6BF6",
                        color: "#fff",
                        borderRadius: "8px",
                        padding: "8px 24px",
                        fontSize: "14px",
                        fontWeight: "500",
                        textTransform: "none",
                        "&:hover": {
                          backgroundColor: "#3B5CE6",
                        },
                        "&:disabled": {
                          backgroundColor: "#E5E7EB",
                          color: "#9CA3AF",
                        },
                      }}
                    >
                      {(activeMainTab === 0 && isSavingProposal) ||
                      (activeMainTab === 1 && isSavingBOQs) ||
                      (activeMainTab === 2 && isSavingMilestones) ? (
                        <>
                          <CircularProgress
                            size={16}
                            sx={{ marginRight: "8px" }}
                          />
                          Saving...
                        </>
                      ) : (
                        "Next"
                      )}
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Right Sidebar */}
            {!isMobile && activeMainTab !== 3 && (
              <ProjectSidebar
                bidDetails={bidDetails}
                isCollapsed={isSidebarCollapsed}
              />
            )}
          </Box>
        </>
      </Box>

      {/* Approve Modal */}
      <ConfirmModel
        visible={isApproved}
        handleClose={() => setIsApproved(false)}
        confirmation={handleApproveBid}
        message={
          <>
            <p>Are you sure you want to approve this bid?</p>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{ flexWrap: "wrap", flex: 1, marginTop: "1vh" }}
            >
              <Box
                component="img"
                src={
                  bidDetails?.project_image?.[0]?.image
                    ? bidDetails?.project_image?.[0]?.image
                    : bidDetails?.default_image?.[0]?.image
                }
                alt="Project Icon"
                sx={{
                  width: 45,
                  height: 45,
                  borderRadius: "8px",
                  border: "2px solid white",
                  boxShadow: "0px 4px 2.67px 0px #00000026",
                  marginRight: 1,
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "normal",
                  fontSize: "14px",
                  fontWeight: "400",
                }}
              >
                {bidDetails?.name || "Bid Approval"}
              </Typography>
            </Box>
          </>
        }
      />
    </div>
  );
}
